/**
 * Calculate exponential backoff delay for retry attempts.
 * Delay increases exponentially with each attempt: 15s, 45s, 135s, 405s, ...
 *
 * @param attemptIndex - The current attempt index (0-indexed)
 * @returns The calculated delay in milliseconds
 *
 * @example
 * getExponentialBackoff(0) // 15000ms (15s)
 * getExponentialBackoff(1) // 45000ms (45s)
 * getExponentialBackoff(2) // 135000ms (2m 15s)
 */
export const getExponentialBackoff = (attemptIndex: number): number => {
  const delay = 15000 * 3 ** attemptIndex;
  return delay;
};

/**
 * Calculate inverse exponential backoff delay for polling.
 * Delay starts at maxDelay and exponentially decreases to minDelay.
 * Useful for polling operations that should check more frequently over time.
 *
 * @param pollCount - The current poll count (0-indexed)
 * @param maxDelay - Maximum delay in milliseconds (initial delay, default: 30000)
 * @param minDelay - Minimum delay in milliseconds (floor, default: 2000)
 * @returns The calculated delay in milliseconds
 *
 * @example
 * // Default: starts at 30s, decreases to 2s
 * getInverseExponentialBackoff(0) // 30000ms (30s)
 * getInverseExponentialBackoff(1) // 15000ms (15s)
 * getInverseExponentialBackoff(2) // 7500ms (7.5s)
 * getInverseExponentialBackoff(3) // 3750ms (3.75s)
 * getInverseExponentialBackoff(4) // 2000ms (clamped to min)
 * getInverseExponentialBackoff(5) // 2000ms (clamped to min)
 */
export const getInverseExponentialBackoff = (
  pollCount: number,
  maxDelay: number = 30000,
  minDelay: number = 2000,
): number => {
  const delay = maxDelay / Math.pow(2, pollCount);
  return Math.max(delay, minDelay);
};
